import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { conversationDetailQueryKey } from "@/api/chat/chat";
import { disableTeamShare, enableTeamShare } from "@/api/shared-conversations";
import type { ITeamShareResponse } from "@/api/shared-conversations";
import {
    sharedConversationQueryOptions,
    sharedConversationsQueryOptions,
} from "@/hooks/useSharedConversation";
import { CLIENT_CONVERSATIONS_KEY } from "@/pages/frontend/clients/components/ClientConversationsTab";

/**
 * Toggle a Polly conversation's org-wide visibility. The mutation variable is
 * the desired next state: `true` → `enableTeamShare`, `false` →
 * `disableTeamShare`. On success it invalidates the four caches the rest of the
 * app reads team-visibility from:
 *   - the conversation's detail query so the chat header's
 *     `useConversationTeamVisible` (and the owner-only toggle it feeds)
 *     reconcile to the server truth.
 *   - ["sharedConversations"] so every teammate's Shared-With-Me list picks the
 *     conversation up (or drops it) on next mount.
 *   - this conversation's share lookup, which is keyed outside that prefix.
 *   - [CLIENT_CONVERSATIONS_KEY] so the client-hub conversation rows
 *     (`ConversationRow` reads `is_team_visible` to pick the "Team" vs
 *     "Shared by {name}" chip) refetch instead of trusting a cross-feature
 *     invariant — today only collaborator rows render that chip and the
 *     toggle is owner-only, but the read set, not that coincidence, is what
 *     keeps the badge honest.
 *
 * The optimistic flip + rollback lives in the toggle component (it owns the
 * switch's checked state and the analytics/toast decisions that depend on the
 * response's `newly_enabled` / `notified_count`); this hook is the thin
 * transport + invalidation. Transport failures (network / 5xx / 429) surface on
 * the mutation's `error` channel as the raw `AxiosError` so the toggle can read
 * `error.response?.status` to distinguish a rate-limit from a generic failure.
 */
export const useTeamShare = (conversationId: number | string) => {
    const queryClient = useQueryClient();

    return useMutation<ITeamShareResponse, AxiosError, boolean>({
        mutationFn: (enable) =>
            enable
                ? enableTeamShare(conversationId)
                : disableTeamShare(conversationId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: sharedConversationsQueryOptions.queryKey,
            });
            queryClient.invalidateQueries({
                queryKey: sharedConversationQueryOptions(String(conversationId))
                    .queryKey,
                exact: true,
            });
            queryClient.invalidateQueries({
                queryKey: conversationDetailQueryKey(String(conversationId)),
            });
            // Prefix-only (every client / q / page variant): this hook doesn't
            // know the conversation's client_id, and invalidateQueries only
            // refetches the client tabs that are actually mounted.
            queryClient.invalidateQueries({
                queryKey: [CLIENT_CONVERSATIONS_KEY],
            });
        },
    });
};
